'use client';

import Icon from '@ant-design/icons';
import { MIND_MODE, WS_MIND_TYPE } from '@mindverse/accessor-open';
import { useMemoizedFn } from 'ahooks';
import { App, message } from 'antd';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { Suspense, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import ChatSvg from '@/components/Icon/svgs/chat.svg';
import RightSvg from '@/components/Icon/svgs/right.svg';
import ConnectSvg from '@/components/Icon/svgs/waving_hand.svg';
// App 组件将在需要时动态导入
import LottieJumpLoading from '@/components/chat-app/components/LottieJumpLoading';
import SmallDotsLoading from '@/components/chat-app/components/SmallDotsLoading';
import type { EventCreateSessionInterface } from '@/components/chat-app/utils/utils';
import { CONTAINER_EVENT } from '@/components/chat-app/utils/utils';
import HomeContext from '@/context/HomeContext';
import { useRegion } from '@/hooks/useAppEnv';
import { useChatManager } from '@/hooks/useChatManager';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import { useModel } from '@/hooks/useModel';
import useOneClick2App from '@/hooks/useOneClick2App';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import { type IAIPlay, getBubble } from '@/service/agent';
import { breakIce, sendFriendRequest } from '@/service/friend';
import type { IHomepageData } from '@/service/homePage';
import {
  HOMEPAGE_FRIEND_STATUS,
  HOMEPAGE_STATUS,
  exposeHomepage,
  getHomepage,
  updateHomepageSettings
} from '@/service/homePage';
import useFriendStore from '@/store/useFriendStore';
import type { IAutoReply, TNotice } from '@/types/wsNotice';
import { ENoticeType } from '@/types/wsNotice';
import { AppStoreLink } from '@/utils/call-app';
import { EVENT } from '@/utils/event';
import { defaultHomeLogo } from '@/utils/operation';
import reportClick, { EReportClickKey } from '@/utils/report/reportClick';
import reportExpose, { EReportExposeKey } from '@/utils/report/reportExpose';
import { ROUTER_PATH } from '@/utils/router';
import {
  copyToClipboard,
  isAndroidWechat,
  isProblematicAndroidBrowser,
  isWeChat
} from '@/utils/utils';

import AddFriendModal from './AddFriendModal';
import HomepageContext, { type PlayModalConfig } from './HomepageContext';
import MoreIcon from './HomepageMoreIcon';
import VoiceIcon from './HomepageVoiceIcon';
import styles from './index.less';

const defaultCover =
  'https://mindverseglobal.mindverse.com/front-img/crm/1759133775950/20250929-161600.png';

// Lazy load conditional components
const ExtensionDrawer = dynamic(
  () => import('@/components/chat-app/components/userInput/ExtensionDrawer'),
  { ssr: false }
);
const LoginModal = dynamic(() => import('@/components/LoginModal'), {
  ssr: false
});
const Introduction = dynamic(() => import('./Introduction'), {
  ssr: false
});
const SlideModal = dynamic(() => import('./SlideModal'), {
  ssr: false
});
const FriendConnectModal = dynamic(() => import('../components/FriendConnectModal'), {
  ssr: false
});
const BackIcon = dynamic(() => import('./BackIcon'), {
  ssr: false
});
const AIGamePlayModal = dynamic(
  () => import('@/components/chat-app/components/userInput/AIGamePlayModal'),
  { ssr: false }
);

interface IProps {
  route: string;
  className?: string;
  type?: string; // 'route' | 'userId';
  initialData?: { data: IHomepageData | null; subCode?: string };
  forceInMobile?: boolean;
  origin?: 'homepage' | 'chat';
}

enum EErrorType {
  NOT_FOUND = 'not_found',
  CLOSED = 'closed',
  BLOCKED = 'blocked'
}

const ErrorImgMap = {
  [EErrorType.NOT_FOUND]:
    'https://mindverseglobal.mindverse.com/front-img/crm/1755496642320/not_found.png',
  [EErrorType.CLOSED]:
    'https://mindverseglobal.mindverse.com/front-img/crm/1755496642321/forbidden.png',
  [EErrorType.BLOCKED]:
    'https://mindverseglobal.mindverse.com/front-img/crm/1755496642320/not_found.png'
};

const ErrorMsgTMap = {
  [EErrorType.NOT_FOUND]: 'homepage.error.notFound',
  [EErrorType.CLOSED]: 'homepage.error.closed',
  [EErrorType.BLOCKED]: 'homepage.error.blocked'
};

const HomePage = (props: IProps) => {
  const {
    route,
    className,
    initialData,
    type = 'route',
    forceInMobile,
    origin = 'homepage'
  } = props;

  const { sessionSocketManager } = useChatManager({ allowAnonymous: true });

  const { userProfile, isLogin, mindDetail, loadUserDetail } = useModel('auth-model', (model) => ({
    userProfile: model.userProfile,
    isLogin: model.isLogin,
    mindDetail: model.mindDetail,
    loadUserDetail: model.loadUserDetail
  }));
  const [layoutSize] = useStorage('layoutSize');
  const [AppComponent, setAppComponent] = useState<any>(null);
  const appRenderedRef = useRef(false);
  const layoutContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const chatScrollContainerRef = useRef<HTMLElement | null>(null);

  const exposeRef = useRef(false);

  const { modal } = App.useApp();

  const [isClient, setIsClient] = useState(false);
  const [inWechat, setInWechat] = useState(false);
  const [inAndroidWechat, setInAndroidWechat] = useState(false);
  const [onlyShowCover, setOnlyShowCover] = useState(false);
  const [inAppClip, setInAppClip] = useState(false); // 在这里特指app clip

  const [homePageData, setHomePageData] = useState<IHomepageData | null>(null);

  // Bubble 相关状态
  const [bubbleData, setBubbleData] = useState<IAIPlay | null>(null);
  const [bubbleLoading, setBubbleLoading] = useState(false);

  const [error, setError] = useState<EErrorType | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const [hideConnectButton, setHideConnectButton] = useState(true);

  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginModalType, setLoginModalType] = useState<'login' | 'initPA'>('login');
  const [shouldLoadLoginModal, setShouldLoadLoginModal] = useState(false);

  const [talkModalOpen, setTalkModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [shouldLoadFriendConnectModal, setShouldLoadFriendConnectModal] = useState(false);
  const [addFriendModalOpen, setAddFriendModalOpen] = useState(false);

  const updateFriendList = useFriendStore((state) => state.updateFriendList);
  const updateUnreadList = useFriendStore((state) => state.updateUnreadList);
  const updateFriendRequestList = useFriendStore((state) => state.updateFriendRequestList);

  const [source, circleRoute, circleName] = useMemo(() => {
    if (typeof window === 'undefined') return ['HOMEPAGE', undefined, undefined] as const;

    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source')?.toUpperCase();

    const validSources = ['QR_CODE', 'NFC', 'SEARCH', 'CIRCLE'];
    const finalSource = validSources.includes(utmSource || '') ? utmSource : 'HOMEPAGE';

    if (finalSource === 'CIRCLE') {
      const _route = urlParams.get('circleRoute');
      const _name = urlParams.get('circleTitle') || urlParams.get('circleName');

      return [finalSource, _route || undefined, _name || undefined] as const;
    }

    return [finalSource, undefined, undefined] as const;
  }, []);

  const { clickToApp, WeChatBrowserOverlay } = useOneClick2App({
    path: 'ai-home',
    params: {
      route: homePageData?.route,
      source,
      ...(circleRoute ? { circleType: circleRoute } : {})
    }
  });

  // 控制 App 组件的加载时机
  const [shouldLoadApp, setShouldLoadApp] = useState(false);
  // 追踪客户端 fetch 是否完成
  const [clientFetchCompleted, setClientFetchCompleted] = useState(false);

  // PlayModal 相关状态
  const [playModalOpen, setPlayModalOpen] = useState(false);
  const [playModalConfig, setPlayModalConfig] = useState<PlayModalConfig | undefined>(undefined);

  const isMine = useMemo(() => {
    return userProfile && homePageData && userProfile.userId == homePageData.userId;
  }, [userProfile, homePageData]);

  // Preload LoginModal after initial render to ensure instant open
  useEffect(() => {
    // Wait 1 second after mount, then preload
    const timer = setTimeout(() => {
      setShouldLoadLoginModal(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Also load immediately if user tries to open before preload completes
  useEffect(() => {
    if (loginModalOpen && !shouldLoadLoginModal) {
      setShouldLoadLoginModal(true);
    }
  }, [loginModalOpen, shouldLoadLoginModal]);

  // Preload FriendConnectModal after initial render
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldLoadFriendConnectModal(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Also load immediately if user tries to open before preload completes
  useEffect(() => {
    if (confirmModalOpen && !shouldLoadFriendConnectModal) {
      setShouldLoadFriendConnectModal(true);
    }
  }, [confirmModalOpen, shouldLoadFriendConnectModal]);

  // 客户端 fetch 完成后加载 App 组件
  // 但如果是自己的主页(isMine)则不加载
  useEffect(() => {
    if (clientFetchCompleted && !isMine && sessionSocketManager) {
      setShouldLoadApp(true);
    }
  }, [clientFetchCompleted, isMine, sessionSocketManager]);

  // 登录用户有数据时立即触发加载(快速加载兜底)
  // 但如果是自己的主页(isMine)则不加载
  useEffect(() => {
    if (
      userProfile &&
      mindDetail &&
      homePageData &&
      sessionSocketManager &&
      userProfile.userId != homePageData.userId &&
      !shouldLoadApp &&
      !isMine &&
      !(
        homePageData.friendStatus !== HOMEPAGE_FRIEND_STATUS.FRIEND &&
        userProfile.homepageStatus === HOMEPAGE_STATUS.CLOSE
      )
    ) {
      setShouldLoadApp(true);
    }
  }, [userProfile, mindDetail, homePageData, shouldLoadApp, isMine, sessionSocketManager]);

  const chatSessionIdRef = useRef<string | undefined>(undefined);

  const [chatSessionId, setChatSessionId] = useState(homePageData?.sessionId || undefined);

  const [appLoading, setAppLoading] = useState(false);

  const [initOpenConfirmModal, setInitOpenConfirmModal] = useState<boolean>(false);

  const { voiceMute } = useContext(HomeContext);

  const { region } = useRegion();

  const [showExtensionDrawer, setShowExtensionDrawer] = useState<boolean>(false);

  const showConnect = useMemo(() => {
    return (
      (!isLogin || userProfile?.userId != homePageData?.userId) &&
      !hideConnectButton &&
      (homePageData?.friendStatus === undefined ||
        [HOMEPAGE_FRIEND_STATUS.STRANGER, HOMEPAGE_FRIEND_STATUS.PENDING_REQUEST].includes(
          homePageData.friendStatus
        ))
    );
  }, [hideConnectButton, homePageData, isLogin, userProfile]);

  const canShowPlayModal = useMemo(() => {
    return (
      !['official_product_assistant'].includes(homePageData?.route || '') &&
      homePageData?.friendStatus === HOMEPAGE_FRIEND_STATUS.FRIEND &&
      !homePageData.isBlocked &&
      !homePageData.blockedByMe &&
      userProfile?.userId
    );
  }, [homePageData, userProfile?.userId]);

  const canFetchBubble = useMemo(() => {
    return (
      isLogin &&
      homePageData?.friendStatus === HOMEPAGE_FRIEND_STATUS.FRIEND &&
      homePageData.userId &&
      homePageData.sessionId &&
      !homePageData.isBlocked &&
      !homePageData.blockedByMe
    );
  }, [homePageData, isLogin]);

  // 稳定 AppComponent 的渲染条件，避免不必要的卸载重新挂载
  const shouldRenderApp = useMemo(() => {
    // 基本条件
    if (!AppComponent || isMine) {
      return false;
    }

    // 关键：如果正在加载数据，保持当前状态不变
    // 避免数据更新时序导致的中间状态
    if (loading) {
      return true;
    }

    // 和setAppComponent内逻辑一致
    const friendClosed = homePageData?.status === HOMEPAGE_STATUS.CLOSE;
    const userCLosed = !(
      homePageData?.friendStatus !== HOMEPAGE_FRIEND_STATUS.FRIEND &&
      userProfile?.homepageStatus === HOMEPAGE_STATUS.CLOSE
    );

    return userCLosed || friendClosed;
  }, [
    AppComponent,
    isMine,
    loading,
    homePageData?.friendStatus,
    userProfile?.homepageStatus,
    homePageData?.status
  ]);

  const router = useRouter();
  const { t } = useTranslation();
  const handleClickHome = async () => {
    if (isLogin) {
      router.push(ROUTER_PATH.personalAI);
    } else {
      window.location.href =
        region == 'cn'
          ? 'https://intro.second-me.cn?utm_source=agentShare'
          : 'https://home.second.me?utm_source=agentShare';
    }
  };

  useEffect(() => {
    setIsClient(true);
    const _isWechat = isWeChat();
    const _isAndroidWechat = isAndroidWechat();
    const _onlyShowCover = isProblematicAndroidBrowser();

    setInWechat(_isWechat);
    setInAndroidWechat(_isAndroidWechat);
    setOnlyShowCover(_onlyShowCover);

    if ((window as any).inApp) {
      setInAppClip(true);
      const top = (window as any).SAFE_AREA_TOP ?? 0;

      document.documentElement.style.setProperty('--safe-top', `${top}px`);
    }

    return () => {
      document.documentElement.style.removeProperty('--safe-top');
    };
  }, []);

  const inMobile = useMemo(
    () =>
      typeof forceInMobile === 'boolean'
        ? forceInMobile
        : isClient && layoutSize === LAYOUT_SIZE.SMALL,
    [layoutSize, isClient, forceInMobile]
  );

  const checkError = (subCode?: string) => {
    switch (subCode) {
      case 'route.user.not.found':
        setError(EErrorType.NOT_FOUND);

        return EErrorType.NOT_FOUND;

      case 'route.user.is.blocked':
        setError(EErrorType.BLOCKED);

        return EErrorType.BLOCKED;

      case 'homepage.access.forbidden':
        setError(EErrorType.CLOSED);

        return EErrorType.CLOSED;

      default:
        setError(undefined);

        return undefined;
    }
  };

  const initHomepageData = useMemoizedFn(() => {
    if (initialData) {
      setHomePageData(initialData.data);
      setChatSessionId(initialData.data?.sessionId);
      chatSessionIdRef.current = initialData.data?.sessionId;
      setHideConnectButton(
        initialData.data?.friendStatus === HOMEPAGE_FRIEND_STATUS.REQUEST_RECEIVED
      );
      checkError(initialData.subCode);
    }

    setLoading(true);
    getHomepage(
      type === 'route'
        ? { route }
        : {
            userId: route
          }
    )
      .then((res) => {
        if (res.data.code !== 0) {
          const errorType = checkError(res.data.subCode);

          const msg =
            res.data.message || (errorType ? t(ErrorMsgTMap[errorType]) : t('error.oops'));

          throw new Error(msg);
        }

        if (res.data.data) {
          setError(undefined);
          setHomePageData({
            ...res.data.data,
            isLogin: res.data.data.isLogin || isLogin
          });
          chatSessionIdRef.current = res.data.data.sessionId;
          setChatSessionId(res.data.data.sessionId);
          setHideConnectButton(
            res.data.data.friendStatus === HOMEPAGE_FRIEND_STATUS.REQUEST_RECEIVED
          );
        }
      })
      .catch((e) => {
        message.error(e.message || t('error.oops'));
      })
      .finally(() => {
        setLoading(false);
        setClientFetchCompleted(true);
      });
  });

  const getHomePageData = useMemoizedFn(() => {
    setLoading(true);
    getHomepage(
      type === 'route'
        ? { route }
        : {
            userId: route
          }
    )
      .then((res) => {
        if (res.data.code !== 0) {
          const errorType = checkError(res.data.subCode);

          const msg =
            res.data.message || (errorType ? t(ErrorMsgTMap[errorType]) : t('error.oops'));

          throw new Error(msg);
        }

        if (res.data.data) {
          setError(undefined);
          setHomePageData({
            ...res.data.data,
            isLogin: res.data.data.isLogin || isLogin
          });

          const prevChatSessionId = chatSessionIdRef.current;

          if (appRenderedRef.current) {
            if (
              (!!res.data.data.sessionId || !!prevChatSessionId) &&
              res.data.data.sessionId !== prevChatSessionId
            ) {
              setChatSessionId(res.data.data.sessionId || '');
            }
          }

          setHideConnectButton(
            res.data.data.friendStatus === HOMEPAGE_FRIEND_STATUS.REQUEST_RECEIVED
          );
        }
      })
      .catch((e) => {
        message.error(e.message || t('error.oops'));
      })
      .finally(() => {
        setLoading(false);
        setClientFetchCompleted(true);
      });
  });

  const fetchBubble = useMemoizedFn((forceRefresh = false) => {
    if (!homePageData?.userId || !homePageData.sessionId || !canFetchBubble) return;

    setBubbleLoading(true);
    getBubble({
      friendId: homePageData.userId,
      sessionId: homePageData.sessionId,
      forceRefresh
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message);
        }

        setBubbleData(res.data.data);
      })
      .catch((e) => {
        message.error(e.message || t('error.oops'));
      })
      .finally(() => {
        setBubbleLoading(false);
      });
  });

  useEffect(() => {
    initHomepageData();
  }, [initHomepageData, route, type]);

  useEffect(() => {
    if (canFetchBubble) {
      fetchBubble();
    }
  }, [fetchBubble, canFetchBubble]);

  useEffect(() => {
    // Wait for isClient to ensure inAppClip has been determined
    if (homePageData && !exposeRef.current && origin === 'homepage' && isClient) {
      reportExpose(EReportExposeKey.ai_home_expose, {
        key1: homePageData.route,
        key2: homePageData.userId,
        key3: userProfile?.userId,
        key4: false,
        key5: inAppClip ? 'true' : 'false'
      });
      sessionStorage.setItem('visitHomepageUserId', homePageData.userId || '');
      localStorage.setItem('homepageUserId', homePageData.userId || '');
      exposeHomepage(homePageData.route || route);
      exposeRef.current = true;
    }

    return () => {
      sessionStorage.removeItem('visitHomepageUserId');
    };
  }, [homePageData, userProfile, route, origin, inAppClip, isClient]);

  useEffect(() => {
    if (isClient && isLogin) {
      const urlParams = new URLSearchParams(window.location.search);
      const connectParam = urlParams.get('connect');

      if (connectParam === 'true') {
        setInitOpenConfirmModal(true);

        // 移除 connect 参数
        urlParams.delete('connect');

        // 构建新的 URL，保留其他参数
        const newPath = window.location.pathname;
        const newSearch = urlParams.toString() ? `?${urlParams.toString()}` : '';
        const newUrl = `${newPath}${newSearch}${window.location.hash}`;

        // 替换当前 URL 但不刷新页面
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [isClient, isLogin]);

  // 处理自动连接
  useEffect(() => {
    if (showConnect && initOpenConfirmModal) {
      // 清除autoConnect状态，防止重复触发
      setInitOpenConfirmModal(false);
      // 延迟一点执行，确保页面已完全加载
      setTimeout(() => {
        setConfirmModalOpen(true);
        reportClick(EReportClickKey.homepage_ice_break_clicked, {
          key1: 'pageBtn',
          key2: homePageData?.userId,
          key3: userProfile?.userId
        });
        handleBreakIce();
      }, 300);
    }
  }, [initOpenConfirmModal, showConnect, homePageData, userProfile]);

  useEffect(() => {
    if (inWechat) {
      setTimeout(() => {
        if (window.WeixinJSBridge) {
          window.WeixinJSBridge.invoke('getNetworkType', {}, () => {
            videoRef.current?.play();
          });
        }
      }, 500);
    }
  }, [inWechat]);

  // 当满足加载条件时,动态导入 App 组件
  // 条件1: shouldLoadApp 为 true (未登录用户1秒后 或 登录用户有数据时)
  // 条件2: 登录用户需要满足原有的业务逻辑判断
  useEffect(() => {
    if (!shouldLoadApp || AppComponent || appLoading) {
      return;
    }

    // 除了 ‘我关了’ && ‘对方没关’的情况，其余三种开关情况都要渲染
    const friendClosed = homePageData?.status === HOMEPAGE_STATUS.CLOSE;
    const userCLosed = !(
      homePageData?.friendStatus !== HOMEPAGE_FRIEND_STATUS.FRIEND &&
      userProfile?.homepageStatus === HOMEPAGE_STATUS.CLOSE
    );

    // 未登录用户或登录用户都可以加载
    const shouldLoadForLoggedInUser =
      userProfile &&
      mindDetail &&
      homePageData &&
      userProfile.userId != homePageData.userId &&
      (friendClosed || userCLosed);
    const shouldLoadForGuest = !userProfile || !isLogin;

    if (shouldLoadForLoggedInUser || shouldLoadForGuest) {
      setAppLoading(true);
      import('@/components/chat-app')
        .then((module) => {
          setAppComponent({
            App: module.default,
            EAppMode: module.EAppMode
          });
          console.log('App component loaded successfully');
        })
        .catch((err) => {
          console.error('Failed to load App component:', err);
          setAppLoading(false);
        });
    }
  }, [shouldLoadApp, userProfile, mindDetail, AppComponent, appLoading, homePageData, isLogin]);

  // 使用单独的 useEffect 监听 AppComponent 的变化，确保组件渲染后执行回调 用于chat懒加载
  useEffect(() => {
    if (!!AppComponent && !appRenderedRef.current && !!homePageData?.userId) {
      // 使用 setTimeout 确保事件监听器已经注册完成
      // requestAnimationFrame 可能太快，导致事件监听器还没注册
      const timer = setTimeout(() => {
        // 标记已执行回调，避免重复执行
        appRenderedRef.current = true;
        //
        console.log('123123123 homepage', homePageData.isLogin, homePageData.fpAccountExists);

        if (homePageData.isLogin || homePageData.fpAccountExists) {
          handleCreateSession();
        }

        const scrollContainer = document.getElementById('chat-scroll-container');

        chatScrollContainerRef.current = scrollContainer;
        setAppLoading(false);
        console.log('App component rendered, callback executed');
      }, 100); // 延迟 100ms 确保事件监听器已注册

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [AppComponent, homePageData]);

  useEffect(() => {
    const handleShowTalks = () => {
      setTalkModalOpen(true);
    };

    addEventListener(EVENT.HOMEPAGE_SHOW_TALKS, handleShowTalks);

    return () => removeEventListener(EVENT.HOMEPAGE_SHOW_TALKS, handleShowTalks);
  }, []);

  const handlePrivateChange = useMemoizedFn(() => {
    const current = userProfile?.homepageStatus;
    const target = current === HOMEPAGE_STATUS.OPEN ? HOMEPAGE_STATUS.CLOSE : HOMEPAGE_STATUS.OPEN;

    updateHomepageSettings({
      status: target
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message);
        }

        window.location.reload();
      })
      .catch((err) => {
        message.error(err.message);
      });
  });

  const handleClickChatPlaceholder = () => {
    // if (!isLogin) {
    //   handleLogin();

    //   return;
    // }

    if (
      homePageData?.friendStatus !== HOMEPAGE_FRIEND_STATUS.FRIEND &&
      userProfile?.homepageStatus === HOMEPAGE_STATUS.CLOSE
    ) {
      modal.confirm({
        title: t('homepage.sendPrivate'),
        onOk: () => {
          handlePrivateChange();
        },
        okText: t('homepageEdit.makePublic'),
        cancelText: t('common.ok')
      });
    }
  };

  const handleLogin = () => {
    if (isLogin) {
      return;
    }

    setLoginModalOpen(true);
  };

  useEffect(() => {
    const showLoginModal = (e: Event) => {
      const event = e as CustomEvent;
      const detail = event.detail as { open?: boolean; type?: 'login' | 'initPA' };

      setLoginModalOpen(detail.open ?? false);
      setLoginModalType(detail.type ?? 'login');
    };

    addEventListener(EVENT.HOMEPAGE_SHOW_LOGIN_MODAL, showLoginModal);

    return () => {
      removeEventListener(EVENT.HOMEPAGE_SHOW_LOGIN_MODAL, showLoginModal);
    };
  }, []);

  const handleShare = () => {
    reportClick(EReportClickKey.ai_home_share_clicked, {
      key1: homePageData?.route,
      key2: homePageData?.userId,
      key3: userProfile?.userId
    });
    copyToClipboard(window.location.href).then(() => {
      message.success(t('common.copied'));
    });
  };

  const [breakIceSessionId, setBreakIceSessionId] = useState<string>(
    homePageData?.breakIceSession || ''
  );

  useEffect(() => {
    if (homePageData?.breakIceSession) {
      setBreakIceSessionId(homePageData.breakIceSession);
    }
  }, [homePageData?.breakIceSession]);

  useEffect(() => {
    if (
      homePageData?.friendStatus === HOMEPAGE_FRIEND_STATUS.STRANGER &&
      homePageData.userId &&
      isLogin &&
      source === 'NFC'
    ) {
      sendFriendRequest({
        targetUserId: homePageData.userId,
        source
      }).finally(() => {
        setHomePageData((pre) => ({
          ...pre,
          friendStatus: HOMEPAGE_FRIEND_STATUS.REQUEST_RECEIVED
        }));
      });
    }
  }, [homePageData?.friendStatus, homePageData?.userId, source, isLogin]);

  const handleBreakIce = useMemoizedFn(() => {
    if (breakIceSessionId) {
      return;
    }

    // if (homePageData?.friendStatus === HOMEPAGE_FRIEND_STATUS.STRANGER && homePageData.userId) {
    //   sendFriendRequest({
    //     targetUserId: homePageData.userId,
    //     source,
    //     ...(circleRoute ? { sourceData: { circleRoute } } : {})
    //   }).finally(() => {
    //     setHomePageData((pre) => ({
    //       ...pre,
    //       friendStatus: HOMEPAGE_FRIEND_STATUS.REQUEST_RECEIVED
    //     }));
    //   });
    // }

    breakIce({ friendId: homePageData?.userId || '' })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message);
        }

        setBreakIceSessionId(res.data.data);
      })
      .catch((err) => {
        console.error('Break ice error:', err);
      });
  });

  const handleCreateSession = useMemoizedFn((sid?: string) => {
    const _sid = sid ?? chatSessionIdRef.current;

    console.log('123123123 send event');
    dispatchEvent(
      new CustomEvent<EventCreateSessionInterface>(CONTAINER_EVENT.CREATE_SESSION, {
        detail: {
          params: {
            originSessionId: _sid,
            newSession: _sid ? false : true,
            extParams: {
              sessionType: 'FRIEND',
              memberUserIds: [homePageData?.userId || ''],
              greetingText: homePageData?.greeting || ''
            }
          },
          callback: (__sid) => {
            chatSessionIdRef.current = __sid;
            setChatSessionId(__sid);
          }
        }
      })
    );
  });

  useEffect(() => {
    if (chatSessionId !== chatSessionIdRef.current && !!sessionSocketManager) {
      handleCreateSession(chatSessionId);
    }
  }, [chatSessionId, handleCreateSession, sessionSocketManager]);

  useEffect(() => {
    if (homePageData?.fpAccountExists && !homePageData.sessionId && !!sessionSocketManager) {
      const startTime = Date.now();
      const maxWaitTime = 3000;
      const checkInterval = 100;

      const checkSocketAndCreate = () => {
        const isSocketOpen = sessionSocketManager.getSocket()?.wsId;

        console.log('123123123 check', isSocketOpen);

        if (isSocketOpen) {
          handleCreateSession('');

          return;
        }

        const elapsedTime = Date.now() - startTime;

        if (elapsedTime < maxWaitTime) {
          setTimeout(checkSocketAndCreate, checkInterval);
        } else {
          handleCreateSession('');
        }
      };

      checkSocketAndCreate();
    }
  }, [
    handleCreateSession,
    homePageData?.fpAccountExists,
    homePageData?.sessionId,
    sessionSocketManager
  ]);

  useEffect(() => {
    const handleBreakIceBtn = () => {
      setConfirmModalOpen(true);
      reportClick(EReportClickKey.homepage_ice_break_clicked, {
        key1: 'chatBtn',
        key2: homePageData?.userId,
        key3: userProfile?.userId
      });
      handleBreakIce();
    };

    addEventListener(EVENT.HOMEPAGE_BREAK_ICE, handleBreakIceBtn);

    return () => {
      removeEventListener(EVENT.HOMEPAGE_BREAK_ICE, handleBreakIceBtn);
    };
  }, [handleBreakIce, homePageData, userProfile]);

  const renderSlideModal = useMemoizedFn(() => {
    if (!homePageData?.briefIntroduction && !homePageData?.featuredNum) return null;

    if (homePageData.status === HOMEPAGE_STATUS.CLOSE) return null;

    const ele = (
      <SlideModal
        homePageData={homePageData}
        onCancel={() => {
          setTalkModalOpen(false);
        }}
        open={talkModalOpen}
      />
    );

    if (inMobile) {
      return ele;
    }

    if (layoutContainerRef.current) {
      return createPortal(ele, layoutContainerRef.current);
    }

    return null;
  });

  useEffect(() => {
    const wsNotice = (event: Event) => {
      const sessionId = homePageData?.sessionId;
      const e = event as CustomEvent;
      const data: TNotice = e.detail as TNotice;

      if (data.sourceType === ENoticeType.autoReply) {
        const _data = data.sourceCustom as IAutoReply;

        if (_data.sessionId === sessionId) {
          setHomePageData((pre) => {
            return {
              ...pre,
              autoReply: _data.autoReply
            };
          });
        }
      }
    };

    addEventListener(EVENT.WS_NOTICE, wsNotice);

    return () => {
      removeEventListener(EVENT.WS_NOTICE, wsNotice);
    };
  }, [homePageData]);

  useEffect(() => {
    // 切换的时候初始化
    setTalkModalOpen(false);
  }, [inMobile]);

  useEffect(() => {
    const handleFriendConfirmed = (e: Event) => {
      const event = e as CustomEvent<{ friendUserId?: string }>;
      const { friendUserId } = event.detail || {};

      // 如果是当前查看的用户，更新状态为好友
      if (friendUserId && friendUserId === homePageData?.userId) {
        setHomePageData((pre) => ({
          ...pre,
          friendStatus: HOMEPAGE_FRIEND_STATUS.FRIEND
        }));
      }
    };

    addEventListener(EVENT.HOMEPAGE_FRIEND_CONFIRMED, handleFriendConfirmed);

    return () => {
      removeEventListener(EVENT.HOMEPAGE_FRIEND_CONFIRMED, handleFriendConfirmed);
    };
  }, [homePageData?.userId]);

  if (!isClient) {
    return (
      <div className={classNames(styles.container, className)}>
        <LottieJumpLoading className="!w-16 !h-16 !mt-[24%]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={classNames(styles.container, className)}>
        <div className={classNames(styles.headerContainer, { [styles.inMobile]: inMobile })}>
          <img alt="" className={styles.icon} onClick={handleClickHome} src={defaultHomeLogo} />
          <div className={classNames(styles.text, 'mr-auto')} onClick={handleClickHome}>
            {'Second Me'}
          </div>
        </div>
        <div className={classNames(styles.layoutContainer, { [styles.inMobile]: inMobile })}>
          <div className="flex flex-col items-center mt-[30vh] w-full h-full">
            <img className="w-[120px] h-[120px] object-cover" src={ErrorImgMap[error]} />
            <div className="mt-4 font-medium text-base leading-7 text-primary-new-black">
              {t(ErrorMsgTMap[error])}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <HomepageContext.Provider
      value={{
        homepageData: homePageData,
        setHomepageData: setHomePageData,
        refreshHomepageData: getHomePageData,
        bubbleData,
        setBubbleData,
        bubbleLoading,
        setBubbleLoading,
        fetchBubble: fetchBubble,
        drawerOpen: showExtensionDrawer,
        setDrawerOpen: setShowExtensionDrawer,
        playModalOpen,
        setPlayModalOpen,
        modalConfig: playModalConfig,
        setModalConfig: setPlayModalConfig
      }}
    >
      <div
        className={classNames(styles.container, className)}
        onClick={() => {
          setInAndroidWechat(false);
          videoRef.current?.play();
        }}
      >
        {/* background */}
        {homePageData ? (
          <div className={styles.backgroundContainer}>
            {(() => {
              // Determine background image source
              const getBackgroundSrc = () => {
                return homePageData.cover || homePageData.avatar || defaultCover;
              };

              // Check if should render video (mobile only, has video, not problematic browser)
              const shouldRenderVideo = inMobile && !!homePageData.video && !onlyShowCover;

              if (shouldRenderVideo) {
                return (
                  <>
                    <video
                      ref={videoRef}
                      autoPlay
                      className={styles.backgroundImg}
                      crossOrigin="anonymous"
                      data-object-fit="cover"
                      loop
                      muted
                      onPlaying={() => {
                        // 视频开始播放时隐藏 cover
                        const coverImg = videoRef.current?.nextElementSibling as HTMLImageElement;

                        if (coverImg && !inAndroidWechat) {
                          coverImg.style.opacity = '0';
                        }
                      }}
                      playsInline
                      preload="metadata"
                      src={homePageData.video || ''}
                      webkit-playsinline="true"
                      x5-playsinline="true"
                      x5-video-orientation="portrait"
                      x5-video-player-fullscreen="false"
                      x5-video-player-type="h5"
                    />
                    {/* Cover 图片覆盖在视频上，视频播放后淡出 */}
                    <img
                      alt=""
                      className={classNames(styles.backgroundImg)}
                      crossOrigin="anonymous"
                      src={homePageData.cover || defaultCover}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        transition: 'opacity 0.3s ease',
                        pointerEvents: 'none'
                      }}
                    />
                  </>
                );
              }

              // Fallback to static image
              return (
                <img
                  alt=""
                  className={styles.backgroundImg}
                  crossOrigin="anonymous"
                  src={getBackgroundSrc()}
                />
              );
            })()}

            {inMobile ? null : <div className={styles.backgroundImgFilter} />}
          </div>
        ) : null}

        {/* safe area */}
        {inAppClip ? <div className="h-[var(--safe-top)]" /> : null}
        {/* header */}
        <div className={classNames(styles.headerContainer, { [styles.inMobile]: inMobile })}>
          {origin === 'chat' ? (
            <BackIcon
              className={classNames(styles.voiceIcon, 'mr-auto')}
              iconClassName="!text-[#FEFDFF]"
            />
          ) : inMobile ? (
            <div
              className="flex rounded-[28px] px-[11px] py-[7px] gap-[6px] items-center border border-solid border-[rgba(254,253,255,0.1)] backdrop-blur-[40px] bg-[rgba(88,88,88,0.24)] w-fit mr-auto"
              onClick={() => {
                if (inAppClip) {
                  reportClick(EReportClickKey.get_app_clicked);

                  window.location.href = AppStoreLink;

                  return;
                }

                clickToApp();
              }}
            >
              <img alt="" className={'w-[32px] h-[18px] object-cover'} src={defaultHomeLogo} />
              <div className={'text-xs leading-5 font-medium text-primary-white'}>
                {t('homepage.openInApp')}
              </div>
            </div>
          ) : (
            <>
              <img alt="" className={styles.icon} onClick={handleClickHome} src={defaultHomeLogo} />
              <div className={classNames(styles.text, 'mr-auto')} onClick={handleClickHome}>
                {'Second Me'}
              </div>
            </>
          )}
          {showConnect ? (
            <div
              className={styles.connectContainer}
              onClick={(e) => {
                e.stopPropagation();

                if (!isLogin) {
                  handleLogin();

                  return;
                }

                if (userProfile?.homepageStatus === HOMEPAGE_STATUS.CLOSE) {
                  handleClickChatPlaceholder();

                  return;
                }

                setAddFriendModalOpen(true);
              }}
            >
              <Icon className={styles.connectIcon} component={ConnectSvg} />
              <span>{t('homepage.addFriend')}</span>
            </div>
          ) : null}
          {(homePageData?.friendStatus === HOMEPAGE_FRIEND_STATUS.FRIEND ||
            homePageData?.status === HOMEPAGE_STATUS.OPEN) && (
            <VoiceIcon className={styles.voiceIcon} iconClassName="!text-[#FEFDFF]" />
          )}
          {!isMine && isLogin && homePageData && (
            <MoreIcon
              className={styles.voiceIcon}
              homePageData={homePageData}
              iconClassName="!text-[#FEFDFF]"
            />
          )}
        </div>

        {/* layout */}
        <div
          ref={layoutContainerRef}
          className={classNames(styles.layoutContainer, { [styles.inMobile]: inMobile })}
          id="homepage-layout-container"
        >
          {loading && !homePageData ? (
            <LottieJumpLoading className="!w-16 !h-16 !mt-[24%]" />
          ) : (
            <>
              {!inMobile && (
                <div className={styles.backgroundContainer}>
                  {homePageData?.video && !onlyShowCover ? (
                    <>
                      <video
                        ref={videoRef}
                        autoPlay
                        className={styles.backgroundImg}
                        crossOrigin="anonymous"
                        data-object-fit="cover"
                        loop
                        muted
                        onPlaying={() => {
                          // 视频开始播放时隐藏 cover
                          const coverImg = videoRef.current?.nextElementSibling as HTMLImageElement;

                          if (coverImg) {
                            coverImg.style.opacity = '0';
                          }
                        }}
                        playsInline
                        preload="metadata"
                        src={homePageData.video || ''}
                        webkit-playsinline="true"
                        x5-playsinline="true"
                        x5-video-orientation="portrait"
                        x5-video-player-fullscreen="false"
                        x5-video-player-type="h5"
                      />
                      {/* Cover 图片覆盖在视频上,视频播放后淡出 */}
                      <img
                        alt=""
                        className={classNames(styles.backgroundImg)}
                        crossOrigin="anonymous"
                        src={homePageData.cover || defaultCover}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          transition: 'opacity 0.3s ease',
                          pointerEvents: 'none'
                        }}
                      />
                    </>
                  ) : (
                    <img
                      alt=""
                      className={classNames(styles.backgroundImg)}
                      crossOrigin="anonymous"
                      src={homePageData?.cover || defaultCover}
                    />
                  )}
                </div>
              )}

              {homePageData?.friendStatus === HOMEPAGE_FRIEND_STATUS.FRIEND &&
              !homePageData.blockedByMe &&
              !homePageData.isBlocked ? (
                <ExtensionDrawer
                  onClose={() => setShowExtensionDrawer(false)}
                  open={showExtensionDrawer}
                />
              ) : null}

              <div className={classNames(styles.contentContainer, { [styles.inMobile]: inMobile })}>
                {/* 登录后到chat中显示 */}
                {!shouldRenderApp ? <Introduction homePageData={homePageData} /> : null}
              </div>

              {shouldRenderApp ? (
                <Suspense>
                  <AppComponent.App
                    chatContainer={{
                      appMode: inMobile
                        ? AppComponent.EAppMode.CHAT_MOBILE
                        : AppComponent.EAppMode.CHAT_MODE,
                      hideQuickNote: true,
                      autoCreateSession: false,
                      chatType: 'homepage',
                      config: {
                        mindConfig: {
                          mindId: mindDetail?.shadowMindId ?? '',
                          mode: MIND_MODE.PERSONAL,
                          mindType: WS_MIND_TYPE.shadow
                        },
                        voiceMute: voiceMute,
                        userId: userProfile?.userId,
                        sessionManager: sessionSocketManager
                      }
                    }}
                    className={classNames(styles.chatContainer)}
                  />
                </Suspense>
              ) : isMine ||
                (homePageData?.friendStatus !== HOMEPAGE_FRIEND_STATUS.FRIEND &&
                  userProfile?.homepageStatus === HOMEPAGE_STATUS.CLOSE) ? (
                <div
                  className={classNames(
                    styles.chatContainer,
                    'w-full max-w-[756px] mt-[24px] !h-[44px] flex items-center p-1 gap-[4px] bg-[rgba(0,0,0,0.5)] rounded-[46px] backdrop-blur-[80px]  border-[1px] border-solid border-[rgba(254,253,255,0.2)] cursor-pointer',
                    isMine ? 'cursor-not-allowed' : ''
                  )}
                  onClick={isMine ? handleShare : handleClickChatPlaceholder}
                >
                  {isMine ? null : (
                    <Icon
                      className="text-[24px] p-[6px] text-[rgba(254,253,255,0.5)]"
                      component={ChatSvg}
                    />
                  )}
                  <div
                    className={classNames(
                      'text-base leading-7 text-[rgba(254,253,255,0.5)]',
                      isMine ? 'text-center w-full' : ''
                    )}
                  >
                    {isMine
                      ? t('homepage.shareYourSecondMe')
                      : t('homepage.chatWith', { name: homePageData?.username || '' })}
                  </div>
                  <Icon
                    className="text-[24px] ml-auto p-[6px] text-[rgba(254,253,255,0.5)]"
                    component={RightSvg}
                  />
                </div>
              ) : (
                <div
                  className={classNames(
                    styles.chatContainer,
                    'w-full max-w-[756px] mt-[24px] !h-[48px] flex items-center px-[15px] gap-[4px] bg-[white] rounded-[24px] border-[1px] border-solid border-[fff] cursor-not-allowed'
                  )}
                >
                  <div className={classNames('text-sm leading-6 text-[#afaeae]')}>
                    {t('homepage.initializing')}
                  </div>
                  <SmallDotsLoading className={'ml-auto'} />
                </div>
              )}
            </>
          )}
        </div>

        {renderSlideModal()}

        {WeChatBrowserOverlay}

        <AddFriendModal
          circleName={circleName}
          detail={homePageData}
          handleConfirmCb={() => {
            setHideConnectButton(true);

            updateFriendRequestList();
            updateUnreadList({ isRefresh: true });
            updateFriendList({ isRefresh: true });

            // dispatchEvent(
            //   new CustomEvent(EVENT.HOMEPAGE_FRIEND_CONFIRMED, {
            //     detail: { friendUserId: homePageData?.userId }
            //   })
            // );
          }}
          onClose={() => setAddFriendModalOpen(false)}
          open={addFriendModalOpen}
          source={source}
        />

        {shouldLoadFriendConnectModal && (
          <FriendConnectModal
            breakIceSessionId={breakIceSessionId}
            commonFriendNum={homePageData?.commonFriendNum || 0}
            commonInterests={homePageData?.commonInterests || []}
            handleConfirmCb={() => {
              setHideConnectButton(true);

              updateFriendRequestList();
              updateUnreadList({ isRefresh: true });
              updateFriendList({ isRefresh: true });

              dispatchEvent(
                new CustomEvent(EVENT.HOMEPAGE_FRIEND_CONFIRMED, {
                  detail: { friendUserId: homePageData?.userId }
                })
              );
            }}
            handleContinueChat={() => {
              setConfirmModalOpen(false);
            }}
            handleOpenAddFriendModal={() => {
              setAddFriendModalOpen(true);
            }}
            onCancel={() => {
              setConfirmModalOpen(false);
            }}
            open={confirmModalOpen}
            status={
              homePageData?.friendStatus === HOMEPAGE_FRIEND_STATUS.REQUEST_RECEIVED
                ? 'pending'
                : homePageData?.friendStatus === HOMEPAGE_FRIEND_STATUS.PENDING_REQUEST
                  ? 'confirm'
                  : homePageData?.friendStatus === HOMEPAGE_FRIEND_STATUS.FRIEND
                    ? 'added'
                    : 'add'
            }
            targetAvatar={homePageData?.avatar || ''}
            targetName={homePageData?.username || ''}
            targetUserId={homePageData?.userId || ''}
          />
        )}

        {shouldLoadLoginModal && (
          <LoginModal
            handleClose={() => {
              setLoginModalOpen(false);

              if (isLogin) {
                loadUserDetail();
              }
            }}
            loginSuccessCb={() => {
              getHomePageData();
              loadUserDetail();
            }}
            open={loginModalOpen}
            openAppRoute={{
              path: 'ai-home',
              params: {
                route: homePageData?.route,
                source,
                ...(circleRoute ? { circleType: circleRoute } : {})
              }
            }}
            type={loginModalType}
          />
        )}

        {/* 统一PlayModal实例 */}
        {canShowPlayModal ? (
          <AIGamePlayModal
            bubbleData={playModalConfig?.bubbleData}
            canSelectMention={playModalConfig?.canSelectMention}
            instruction={playModalConfig?.instruction}
            onClose={() => setPlayModalOpen(false)}
            open={playModalOpen}
            origin={playModalConfig?.origin}
            originChoose={playModalConfig?.originChoose}
            originCreator={playModalConfig?.originCreator}
            type={playModalConfig?.type || 'topic'}
          />
        ) : null}
      </div>
    </HomepageContext.Provider>
  );
};

export default HomePage;
